package ai.jessie.web.pages.organisations

import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.projects.LocalProjectPort
import ai.jessie.web.projects.PROJECTS_TITLE
import ai.jessie.web.projects.ProjectState
import ai.jessie.web.projects.ProjectUiState
import ai.jessie.web.projects.projectHeaderActions
import ai.jessie.web.projects.projectRecords
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.Kpi
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.crumb
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

// UC-010. Tenant state is keyed by the routed organisation, so a change of organisation discards
// it together with any unsaved project structure.
@Page("{organisationId}/projects")
@Composable
fun organisationProjectsPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        projectProblemFrame(organisationId, organisationRequiredProblem())
    } else key(organisationId) { projectsContent(organisationId, null) }
}

/** The project frame around a problem that needs no request, e.g. an invalid link. */
@Composable
internal fun projectProblemFrame(organisationId: String, problem: OnboardingProblem) {
    appFrame(organisationId, projectsHref(organisationId), PROJECTS_TITLE) {
        Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(problem, busy = false) }
    }
}

@Composable
internal fun projectsContent(organisationId: String, projectId: String?) {
    val port = LocalProjectPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, projectId) {
            ProjectState(port, scope, organisationId, projectId)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    val state = model.state
    val authenticationRequired = state.error?.authenticationRequired == true
    // The detail page is titled by the returned record; the list keeps its name.
    val record = state.current?.takeIf { projectId != null }
    // While a form or a confirmation is open, its own controls are the only actions.
    val actionable = state.draft == null && state.change == null && !authenticationRequired
    appFrame(
        organisationId,
        projectsHref(organisationId),
        when {
            projectId == null -> PROJECTS_TITLE
            record != null -> record.name
            else -> DETAIL_TITLE
        },
        subtitle = record?.description,
        kpis = activeKpi(projectId, state),
        authenticationRequired = authenticationRequired,
        crumb =
            if (projectId != null) {
                {
                    crumb(
                        PROJECTS_TITLE,
                        projectsHref(organisationId),
                        record?.name ?: DETAIL_TITLE,
                    )
                }
            } else null,
        actions = projectActions(projectId, state, model, actionable),
    ) {
        projectRecords(organisationId, projectId, state, model.actions)
    }
}

// `12-Projekte.png`: the number of returned projects that are active, once the list is loaded.
private fun activeKpi(projectId: String?, state: ProjectUiState): List<Kpi> {
    val projects = state.projects?.takeIf { projectId == null && state.current == null }
    return listOfNotNull(
        projects?.let { list -> Kpi("Aktiv", list.count { it.active }.toString()) }
    )
}

// List: the primary create action while the list is shown. Detail: edit and lifecycle actions.
private fun projectActions(
    projectId: String?,
    state: ProjectUiState,
    model: ProjectState,
    actionable: Boolean,
): (@Composable () -> Unit)? {
    val record = state.current
    return when {
        !actionable -> null
        projectId == null && state.projects != null && record == null -> {
            {
                themedButton(
                    "Projekt erfassen",
                    model.actions.create,
                    variant = ButtonVariant.PRIMARY,
                    disabled = state.busy,
                )
            }
        }
        projectId != null && record != null -> {
            { projectHeaderActions(record, state.busy, model.actions) }
        }
        else -> null
    }
}

internal fun projectsHref(organisationId: String) = "/organisations/$organisationId/projects"

private const val DETAIL_TITLE = "Projekt"
